/**
 * 会话跟踪工具（PC项目）
 * 用于生成和管理 sessionId 和 traceId，用于 Fundebug 错误追踪
 */

/**
 * 生成唯一ID
 * 格式: 时间戳 + 随机数
 */
function generateUniqueId(prefix: string): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  return `${prefix}_${timestamp}_${random}`;
}

/**
 * 获取或创建 sessionId
 * sessionId 永久有效，存储在 localStorage 中
 * 
 * @returns sessionId
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const SESSION_KEY = 'jufair_session_id';
  
  try {
    // 尝试从 localStorage 获取（永久有效）
    let sessionId = localStorage.getItem(SESSION_KEY);
    
    if (!sessionId) {
      // 生成新的 sessionId
      sessionId = generateUniqueId('sess');
      localStorage.setItem(SESSION_KEY, sessionId);
      
      console.log('🆔 新会话创建（永久）:', sessionId);
    }
    
    return sessionId;
  } catch (e) {
    console.error('获取 sessionId 失败:', e);
    return generateUniqueId('sess');
  }
}

/**
 * 获取或创建 userId（持久化）
 * 如果用户未登录，使用设备指纹作为临时 userId
 * 
 * @returns userId
 */
export function getOrCreateUserId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const USER_KEY = 'jufair_user_id';
  
  try {
    // 尝试从 localStorage 获取
    let userId = localStorage.getItem(USER_KEY);
    
    if (!userId) {
      // 生成设备指纹作为临时 userId
      userId = generateUniqueId('user');
      localStorage.setItem(USER_KEY, userId);
      
      console.log('🆔 新用户ID创建:', userId);
    }
    
    return userId;
  } catch (e) {
    console.error('获取 userId 失败:', e);
    return generateUniqueId('user');
  }
}

/**
 * 生成页面 traceId
 * 每次页面加载或路由切换时生成新的 traceId
 * 
 * @returns traceId
 */
export function generateTraceId(): string {
  const traceId = generateUniqueId('trace');
  
  if (typeof window !== 'undefined') {
    // 存储当前页面的 traceId
    try {
      sessionStorage.setItem('jufair_current_trace_id', traceId);
    } catch (e) {
      console.error('存储 traceId 失败:', e);
    }
  }
  
  return traceId;
}

/**
 * 获取当前页面的 traceId
 * 
 * @returns traceId
 */
export function getCurrentTraceId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return sessionStorage.getItem('jufair_current_trace_id') || generateTraceId();
  } catch (e) {
    console.error('获取 traceId 失败:', e);
    return generateTraceId();
  }
}


/**
 * 获取完整的追踪上下文信息
 * 包含 sessionId, userId, traceId 等
 * 注意：clientIp 由 Fundebug 自动获取，不在此接口中
 */
export interface TrackingContext {
  sessionId: string;
  userId: string;
  traceId: string;
  userAgent: string;
  url: string;
  timestamp: number;
}

export function getTrackingContext(): TrackingContext {
  if (typeof window === 'undefined') {
    return {
      sessionId: '',
      userId: '',
      traceId: '',
      userAgent: '',
      url: '',
      timestamp: Date.now(),
    };
  }

  return {
    sessionId: getOrCreateSessionId(),
    userId: getOrCreateUserId(),
    traceId: getCurrentTraceId(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: Date.now(),
  };
}

/**
 * 从 Cookie 中获取 uid
 */
export function getUidFromCookie(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === 'uid') {
        return value || '';
      }
    }
    return '';
  } catch (e) {
    console.error('获取 Cookie uid 失败:', e);
    return '';
  }
}

/**
 * 编码追踪信息到 Fundebug user.name
 * 格式：sessionId|traceId|uid
 * 
 * @returns 编码后的字符串
 */
export function encodeTrackingToUserName(): string {
  const sessionId = getOrCreateSessionId();
  const traceId = getCurrentTraceId();
  const uid = getUidFromCookie();
  
  // 使用 | 分隔符连接，便于后端解析
  return `${sessionId}|${traceId}|${uid}`;
}

/**
 * 解码 Fundebug user.name 中的追踪信息
 * 
 * @param encodedName 编码后的 name
 * @returns 解码后的对象
 */
export function decodeTrackingFromUserName(encodedName: string): {
  sessionId: string;
  traceId: string;
  uid: string;
} {
  try {
    const [sessionId = '', traceId = '', uid = ''] = encodedName.split('|');
    return { sessionId, traceId, uid };
  } catch (e) {
    console.error('解码追踪信息失败:', e);
    return { sessionId: '', traceId: '', uid: '' };
  }
}

/**
 * 设置已登录用户的 userId
 * 
 * @param userId 用户ID
 */
export function setLoggedInUserId(userId: string): void {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    localStorage.setItem('jufair_user_id', userId);
    
    // 更新 Fundebug 用户信息
    if ((window as any).fundebug) {
      const encodedName = encodeTrackingToUserName();
      (window as any).fundebug.user = {
        id: userId,
        name: encodedName,
      };
    }
    
    console.log('🆔 用户ID已更新:', userId);
  } catch (e) {
    console.error('设置用户ID失败:', e);
  }
}

/**
 * 清除追踪信息（可选，一般不需要调用）
 * 注意：sessionId 和 userId 永久有效，不会被清除
 */
export function clearTrackingInfo(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // 只清除当前的 traceId
    sessionStorage.removeItem('jufair_current_trace_id');
    console.log('🧹 追踪信息已清除（sessionId 和 userId 保持不变）');
  } catch (e) {
    console.error('清除追踪信息失败:', e);
  }
}

